/**
 * OnboardingResearchStatus: what the crawl is doing right now.
 *
 * Two registers, one component:
 * - `variant="list"` (default): the three-row progress list.
 * - `variant="strip"`: one line for the plan step, shown while the job is still
 *   pending behind an already-rendered plan.
 *
 * Announced as `role="status" aria-live="polite"` so a screen reader hears the
 * phase change without the wizard stealing focus. Icons live in
 * `OnboardingIconChip` and are never tinted: the live signal is the teal dot,
 * per DESIGN.md.
 */

import { cn } from "@/lib/utils"
import { OnboardingIconChip } from "./OnboardingIconChip"
import type { OnboardingIconKey } from "./icons"

export type ResearchPhase = "reading" | "extracting" | "drafting" | "done"

type Row = {
  id: ResearchPhase
  label: (domain: string) => string
}

const ROWS: Row[] = [
  { id: "reading", label: (domain) => `Reading ${domain}` },
  { id: "extracting", label: () => "Extracting what you sell" },
  { id: "drafting", label: () => "Drafting 1-2 tests" },
]

const PHASE_ORDER: ResearchPhase[] = ["reading", "extracting", "drafting", "done"]

type RowState = "done" | "current" | "next"

function statusOf(row: ResearchPhase, current: ResearchPhase): RowState {
  const rowI = PHASE_ORDER.indexOf(row)
  const curI = PHASE_ORDER.indexOf(current)
  if (rowI < curI) return "done"
  if (rowI === curI) return "current"
  return "next"
}

const ROW_ICON: Record<RowState, OnboardingIconKey> = {
  done: "state.live",
  current: "state.waiting",
  next: "state.waiting",
}

/** A quiet live marker. Teal is the positive-data tone; the pulse is the motion. */
function LiveDot({ className }: { className?: string }) {
  return (
    <span aria-hidden className={cn("relative flex size-1.5 shrink-0", className)}>
      <span className="absolute inline-flex size-full animate-ping rounded-full bg-success/60 motion-reduce:animate-none" />
      <span className="relative inline-flex size-1.5 rounded-full bg-success" />
    </span>
  )
}

type OnboardingResearchStatusProps = {
  domain: string
  phase: ResearchPhase
  /** `strip` is the single-line form the plan step renders. */
  variant?: "list" | "strip"
  className?: string
}

export function OnboardingResearchStatus({
  domain,
  phase,
  variant = "list",
  className,
}: OnboardingResearchStatusProps) {
  if (variant === "strip") {
    return (
      <div
        role="status"
        aria-live="polite"
        className={cn(
          "flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-border/40 bg-muted/20 px-3 py-2",
          className,
        )}
      >
        <LiveDot />
        <span className="text-xs font-medium text-foreground">
          {phase === "done" ? `Finished reading ${domain}` : `Still reading ${domain}`}
        </span>
        {phase !== "done" && (
          <span className="text-xs text-foreground/70">usually under 20 seconds</span>
        )}
      </div>
    )
  }

  return (
    <div role="status" aria-live="polite" className={cn("space-y-2", className)}>
      {ROWS.map((row) => {
        const state = statusOf(row.id, phase)
        return (
          <div
            key={row.id}
            className={cn(
              "flex items-center gap-3 rounded-lg border px-4 py-3",
              state === "current" && "border-border/60 bg-card",
              state === "done" && "border-border/30 bg-muted/20",
              // No row-level `opacity`: it compounds with the eyebrow's own
              // alpha and drops that text under the AA floor. The hairline and
              // fill carry the recessed state instead.
              state === "next" && "border-border/20 bg-muted/10",
            )}
          >
            <OnboardingIconChip
              icon={ROW_ICON[state]}
              size="sm"
              selected={state === "current"}
              className={cn(
                state === "current" &&
                  "[&_svg]:animate-spin motion-reduce:[&_svg]:animate-none",
              )}
            />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-foreground/70">
                {state === "done" ? "Completed" : state === "current" ? "In progress" : "Up next"}
              </p>
              <p className="flex items-center gap-2 text-sm font-medium text-foreground">
                {row.label(domain)}
                {state === "current" && <LiveDot />}
              </p>
            </div>
          </div>
        )
      })}
    </div>
  )
}
